import {
  getAnswerReferencesForQuestionType,
  parseAnswerReferenceAccessor,
  type AnswerReferenceAccessor,
  type AnswerReferenceResolveArgs,
} from "./answerReferenceRegistry";
import { ANSWER_REFERENCE_PATTERN } from "./referenceTokens";
import type {
  InterviewItem,
  InterviewItemTemplateId,
  QAState,
} from "@/components/interview/types";
import { groupBy } from "@/lib/utils";
import { recordToArray } from "@/utils/helpers";
import type { AudioLanguage } from "@/utils/types";

export type AnswerValuesForTemplate = {
  /** Value used by a bare reference like `{{answer:questionId}}`. */
  default?: string[];
  /** Values used by named references like `{{answer:questionId:md-mostPickedBest}}`. */
  byAccessor?: Partial<Record<AnswerReferenceAccessor, string[]>>;
};

/** Derived lookup used while rendering later question text with answer references. */
export type AnswerValues = Record<
  InterviewItemTemplateId,
  AnswerValuesForTemplate
>;

export const resolveAnswerValuesForTemplate = ({
  statesForItem,
  item,
}: AnswerReferenceResolveArgs): AnswerValuesForTemplate | undefined => {
  if (item.type !== "question" || item.questionType === undefined) {
    return undefined;
  }

  const result: AnswerValuesForTemplate = {};
  const byAccessor: Partial<Record<AnswerReferenceAccessor, string[]>> = {};

  for (const answerReference of getAnswerReferencesForQuestionType(
    item.questionType
  )) {
    const value = answerReference.resolve({ statesForItem, item });
    if (value === undefined) continue;

    if (answerReference.accessor === undefined) {
      result.default = Array.isArray(value) ? value : [value];
    } else {
      byAccessor[answerReference.accessor] = Array.isArray(value)
        ? value
        : [value];
    }
  }

  if (Object.keys(byAccessor).length > 0) {
    result.byAccessor = byAccessor;
  }

  if (result.default === undefined && result.byAccessor === undefined) {
    return undefined;
  }
  return result;
};

/**
 * Groups history the way answer values are resolved, so a reference's picture
 * and its piped label can never come from different states.
 */
export const getStatesByTemplateForItems = (
  history: QAState[],
  items: InterviewItem[]
): Map<InterviewItemTemplateId, AnswerReferenceResolveArgs> => {
  const statesByTemplate = groupBy(history, (state) => state.currentTemplateId);
  const itemById = new Map(items.map((i) => [i.id, i]));
  const resolved = new Map<
    InterviewItemTemplateId,
    AnswerReferenceResolveArgs
  >();
  for (const [templateId, states] of recordToArray(statesByTemplate)) {
    const latestState = states.at(-1);
    const item = latestState ? itemById.get(latestState.currentItemId) : null;
    if (!item) continue;
    resolved.set(templateId, {
      item,
      statesForItem: states.filter((s) => s.currentItemId === item.id),
    });
  }
  return resolved;
};

export const buildAnswerValuesForItems = (
  history: QAState[],
  items: InterviewItem[]
): AnswerValues => {
  const answerValues: AnswerValues = {};
  for (const [templateId, args] of getStatesByTemplateForItems(
    history,
    items
  )) {
    const resolved = resolveAnswerValuesForTemplate(args);
    if (resolved !== undefined) answerValues[templateId] = resolved;
  }
  return answerValues;
};

export const formatAnswerList = ({
  answers,
  language,
  decorateAnswer = (_index, value) => value,
}: {
  answers: string[];
  language: AudioLanguage;
  decorateAnswer?: (index: number, value: string) => string;
}): string =>
  // Split the plain answers: Spanish picks "y" or "e" from the next word.
  new Intl.ListFormat(language, { style: "long", type: "conjunction" })
    .formatToParts(answers)
    .reduce(
      ({ text, answerIndex }, part) =>
        part.type === "element"
          ? {
              text: text + decorateAnswer(answerIndex, answers[answerIndex]),
              answerIndex: answerIndex + 1,
            }
          : { text: text + part.value, answerIndex },
      { text: "", answerIndex: 0 }
    ).text;

const getAnswerReferenceValues = ({
  answerValues,
  templateId,
  rawAccessor,
}: {
  answerValues: AnswerValues;
  templateId: InterviewItemTemplateId;
  rawAccessor: string | undefined;
}): string[] | undefined => {
  const values = answerValues[templateId];
  if (!values) return undefined;

  if (rawAccessor === undefined) {
    return values.default;
  }

  const accessor = parseAnswerReferenceAccessor(rawAccessor);
  return accessor ? values.byAccessor?.[accessor] : undefined;
};

// Example: "Why {{answer:q1}}?" becomes "Why Fast and Reliable?"
// `decorate` wraps each resolved option for the display path only.
/** Wraps one resolved option, so the display path can show its picture. */
export type AnswerReferenceDecorator = (args: {
  templateId: InterviewItemTemplateId;
  rawAccessor: string | undefined;
  index: number;
  value: string;
}) => string;

export const substituteAnswerReferences = ({
  text,
  answerValues,
  language,
  decorate,
}: {
  text: string;
  answerValues: AnswerValues;
  language: AudioLanguage;
  decorate?: AnswerReferenceDecorator;
}): string => {
  const pattern = new RegExp(ANSWER_REFERENCE_PATTERN.source, "g");
  return text.replace(
    pattern,
    (match, rawTemplateId: string, rawAccessor: string | undefined) => {
      // The pattern only captures a uuid, which is what the branded id is.
      const templateId = rawTemplateId as InterviewItemTemplateId;
      const values = getAnswerReferenceValues({
        answerValues,
        templateId,
        rawAccessor,
      });
      if (!values || values.length === 0) return match;
      return formatAnswerList({
        answers: values,
        language,
        decorateAnswer: decorate
          ? (index, value) =>
              decorate({ templateId, rawAccessor, index, value })
          : undefined,
      });
    }
  );
};
